import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { FadeIn } from '../components/ui/AnimatedSection';
import { Check, ShieldCheck, FileText, Smartphone } from 'lucide-react';
import Calculator from '../features/pricing/Calculator';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function Forfaits() {
  const { t } = useTranslation();
  const basePackages = [
    { isPopular: false },
    { isPopular: true },
    { isPopular: false }
  ];
  const translatedPackages = t('forfaits.packages', { returnObjects: true }) as Array<{ name: string; desc: string; features: string[] }>;
  const packages = basePackages.map((pkg, idx) => ({ ...pkg, ...translatedPackages[idx] }));

  return (
    <div className="min-h-screen flex flex-col font-sans text-black-deep bg-cream-100">
      <Navbar />
      
      <main className="flex-grow">
        
        {/* HERO SECTION FORFAITS */}
        <div className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat transition-transform duration-[20s] ease-out hover:scale-110"
            style={{ backgroundImage: 'url("/assets/luxury_dressing.webp")' }}
          >
            <div className="absolute inset-0 bg-black-deep/80"></div>
          </div>
          <FadeIn className="relative z-10 text-center px-4 mt-20">
            <span className="text-gold-400 font-serif italic text-xl md:text-2xl mb-4 block tracking-wide">
              {t('forfaits.heroTag')}
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif text-white leading-tight drop-shadow-lg">
              {t('forfaits.heroTitle1')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-600 italic">{t('forfaits.heroTitle2')}</span>
            </h1>
          </FadeIn>
        </div>

        {/* SECTION FORFAITS PRICING */}
        <section className="py-24 md:py-32 bg-white relative">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold-50 rounded-full blur-[150px] pointer-events-none"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <FadeIn className="text-center mb-20">
              <span className="text-gold-600 tracking-[0.2em] text-xs font-bold uppercase mb-4 block">{t('forfaits.pricingTag')}</span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-black-deep mb-6">{t('forfaits.pricingTitle')}</h2>
              <div className="w-16 h-px bg-gold-400 mx-auto mb-8"></div>
              <p className="text-gray-500 font-light max-w-2xl mx-auto text-lg">
                {t('forfaits.pricingDesc')}
              </p>
            </FadeIn>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
              {packages.map((pkg, idx) => (
                <FadeIn key={idx} delay={idx * 0.15} className={`relative bg-white rounded-2xl flex flex-col transition-all duration-500 ${pkg.isPopular ? 'border-2 border-gold-400 shadow-[0_20px_60px_rgba(201,152,85,0.15)] scale-100 lg:scale-105 z-10' : 'border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-xl'}`}>
                  
                  {pkg.isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-gold-400 to-gold-600 text-white px-6 py-1.5 rounded-full text-xs uppercase tracking-widest font-bold shadow-lg">
                      {t('forfaits.popular')}
                    </div>
                  )}
                  
                  <div className="p-10 border-b border-gray-50 text-center">
                    <h3 className="text-2xl md:text-3xl font-serif text-black-deep mb-4">{pkg.name}</h3>
                    <p className="text-gray-500 font-light text-sm h-12 mb-6">{pkg.desc}</p>
                    
                    <div className="mb-2">
                      <div className="text-gray-400 text-sm line-through mb-1">{t('forfaits.grossPrice')} {70 + idx * 10}€ /h</div>
                      <div className="flex justify-center items-end gap-1">
                        <span className="text-6xl font-serif text-black-deep leading-none">{(70 + idx * 10)/2}€</span>
                        <span className="text-gray-500 text-sm mb-2">{t('forfaits.netPrice')}</span>
                      </div>
                      <div className="text-gold-600 text-xs mt-2 font-light italic">{t('forfaits.netPriceDisclaimer')}</div>
                    </div>
                  </div>

                  <div className="p-10 flex-1 flex flex-col bg-gray-50/50 rounded-b-2xl">
                    <ul className="flex-1 space-y-5 mb-10">
                      {pkg.features.map((feature, fIdx) => (
                        <li key={fIdx} className="flex items-start">
                          <Check className="w-5 h-5 text-gold-500 mr-4 shrink-0 mt-0.5" strokeWidth={2} />
                          <span className="text-sm font-light text-gray-700 leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link to="/audit" className={`w-full py-4 rounded-full uppercase tracking-widest text-xs font-bold transition-all text-center ${pkg.isPopular ? 'bg-gradient-to-r from-gold-400 to-gold-600 text-white shadow-lg hover:shadow-gold-500/30 hover:-translate-y-1' : 'bg-black-deep text-white hover:bg-gray-800'}`}>
                      {t('forfaits.btnAudit')}
                    </Link>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* NEW SECTION: L'AVANCE IMMÉDIATE */}
        <section className="py-24 md:py-40 bg-[#050505] text-white relative overflow-hidden">
          {/* Decorative glow */}
          <div className="absolute inset-0 z-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold-900/15 rounded-full blur-[150px]"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
              
              <div className="w-full lg:w-1/2">
                <FadeIn>
                  <span className="text-gold-400 tracking-[0.3em] text-xs font-bold uppercase mb-6 block flex items-center">
                    <span className="w-8 h-px bg-gold-500 mr-4"></span>
                    {t('forfaits.taxTag')}
                  </span>
                  
                  <h2 className="text-5xl md:text-6xl font-serif text-white leading-tight mb-8">
                    {t('forfaits.taxTitle1')}<br/>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-600 italic">{t('forfaits.taxTitle2')}</span>
                  </h2>
                  
                  <p className="text-2xl font-serif text-gray-300 leading-relaxed mb-6">
                    {t('forfaits.taxP1')}
                  </p>
                  
                  <p 
                    className="text-lg font-light text-gray-400 leading-relaxed mb-10 max-w-lg"
                    dangerouslySetInnerHTML={{ __html: t('forfaits.taxP2') }}
                  ></p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gold-400 mr-4 shrink-0">
                        <FileText size={18} />
                      </div>
                      <div>
                        <h4 className="text-white font-serif text-lg mb-1">{t('forfaits.taxFeature1Title')}</h4>
                        <p className="text-gray-500 text-sm font-light">{t('forfaits.taxFeature1Desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gold-400 mr-4 shrink-0">
                        <Smartphone size={18} />
                      </div>
                      <div>
                        <h4 className="text-white font-serif text-lg mb-1">{t('forfaits.taxFeature2Title')}</h4>
                        <p className="text-gray-500 text-sm font-light">{t('forfaits.taxFeature2Desc')}</p>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              </div>

              {/* Visualization Infographic */}
              <div className="w-full lg:w-1/2">
                <FadeIn delay={0.2}>
                  <div className="bg-[#0a0a0a] border border-white/10 rounded-3xl p-10 md:p-14 shadow-[0_0_100px_rgba(201,152,85,0.05)] relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,rgba(201,152,85,0.1)_0%,transparent_50%)] pointer-events-none"></div>
                    
                    <div className="space-y-8 relative z-10">
                      {/* Ligne 1 : Facture totale */}
                      <div className="flex justify-between items-center pb-8 border-b border-white/5">
                        <span className="text-gray-400 font-light text-lg">{t('forfaits.taxExample')}</span>
                        <span className="text-2xl font-serif text-white">400 €</span>
                      </div>
                      
                      {/* Ligne 2 : Etat */}
                      <div className="flex justify-between items-center pb-8 border-b border-white/5">
                        <div className="flex items-center">
                          <ShieldCheck className="w-6 h-6 text-gold-500 mr-3" />
                          <span className="text-gold-400 font-light text-lg">{t('forfaits.taxState')}</span>
                        </div>
                        <span className="text-2xl font-serif text-gold-400">- 200 €</span>
                      </div>
                      
                      {/* Ligne 3 : Reste à charge */}
                      <div className="flex justify-between items-center pt-4">
                        <span className="text-white text-xl font-medium tracking-wide">{t('forfaits.taxRemaining')}</span>
                        <div className="text-right">
                          <span className="text-6xl font-serif text-white leading-none block mb-2">200 €</span>
                          <span className="text-gold-500/70 text-xs uppercase tracking-widest block">{t('forfaits.taxRemainingSub')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              </div>

            </div>
          </div>
        </section>

        {/* The Calculator as a CTA section */}
        <Calculator />
      </main>

      <Footer />
    </div>
  );
}
